import { userService } from '../../services/userService';
import type { 
  Creator,
  CreatorContact,
  CreatorShipping,
  TikTokProfileData,
  InstagramProfileData,
  TwitchProfileData,
  PlatformServiceResponse
} from './creatorTypes';

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

// Base URL for creators API
const BASE_URL = `${API_BASE_URL}/creators`;

// Base URL for platform services
const PLATFORM_API_BASE_URL = API_BASE_URL;

// API request/response interfaces
export interface CreateContactRequest {
  type: CreatorContact['type'];
  value: string;
  role: CreatorContact['role'];
  status?: CreatorContact['status'];
}

export interface CreateCreatorRequest {
  contacts?: CreateContactRequest[];
  tiktok_data?: Partial<TikTokProfileData>[];
  instagram_data?: Partial<InstagramProfileData>[];
  twitch_data?: Partial<TwitchProfileData>[];
  creator_shipping?: CreatorShipping;
  ticket_uuid?: string;
}

export interface UpdateCreatorRequest {
  contacts?: CreateContactRequest[];
  tiktok_data?: Partial<TikTokProfileData>[];
  instagram_data?: Partial<InstagramProfileData>[];
  twitch_data?: Partial<TwitchProfileData>[];
  creator_shipping?: CreatorShipping;
}

export interface CreatorsResponse {
  data: Creator[];
  total: number;
  filters: {
    pool?: boolean;
    platform?: string;
    search?: string;
    project_uuid?: string;
  };
}

// API Functions - no transformation, direct database structure
export const getCreators = async (filters: {
  pool?: boolean;
  platform?: string;
  search?: string;
  project_uuid?: string;
} = {}): Promise<CreatorsResponse> => {
  try {
    const token = await userService.getAuthToken();
    const queryParams = new URLSearchParams();
    
    if (filters.pool !== undefined) queryParams.set('pool', filters.pool.toString());
    if (filters.platform) queryParams.set('platform', filters.platform);
    if (filters.search) queryParams.set('search', filters.search);
    if (filters.project_uuid) queryParams.set('project_uuid', filters.project_uuid);

    const response = await fetch(`${BASE_URL}?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch creators: ${response.statusText}`);
    }

    const result = await response.json();
    console.log(result.creators);
    
    return {
      data: result.creators || [],
      total: result.count || 0,
      filters,
    };
  } catch (error) {
    console.error('Error fetching creators:', error);
    throw error;
  }
};

export const getCreator = async (creatorId: string): Promise<Creator> => {
  try {
    const token = await userService.getAuthToken();
    
    const response = await fetch(`${BASE_URL}/${creatorId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch creator: ${response.statusText}`);
    }

    const result = await response.json();
    return result.creator || result;
  } catch (error) {
    console.error('Error fetching creator:', error);
    throw error;
  }
};

export const createCreator = async (creator: CreateCreatorRequest): Promise<Creator> => {
  try {
    const token = await userService.getAuthToken();
    
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(creator),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as any));
      if (response.status === 402) {
        // Plan limit reached or payment required
        const friendly = errorData.message || 'Upgrade subscription to add more creators';
        throw new Error(friendly);
      }
      throw new Error(errorData.error || `Failed to create creator: ${response.status}`);
    }

    const result = await response.json();
    return result.creator || result;
  } catch (error) {
    console.error('Error creating creator:', error);
    throw error;
  }
};

export const updateCreator = async (
  creatorId: string, 
  updates: UpdateCreatorRequest
): Promise<Creator> => {
  try {
    const token = await userService.getAuthToken();
    
    const response = await fetch(`${BASE_URL}/${creatorId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update creator: ${response.status}`);
    }

    const result = await response.json();
    return result.creator || result;
  } catch (error) {
    console.error('Error updating creator:', error);
    throw error;
  }
};

export const deleteCreator = async (creatorId: string): Promise<void> => {
  try {
    const token = await userService.getAuthToken();
    
    const response = await fetch(`${BASE_URL}/${creatorId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to delete creator: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting creator:', error);
    throw error;
  }
};

// Platform enrichment service
export const platformService = {
  async searchTikTokUsers(keywords: string, count: number = 8): Promise<PlatformServiceResponse<TikTokProfileData>> {
    try {
      const token = await userService.getAuthToken();
      
      const response = await fetch(
        `${PLATFORM_API_BASE_URL}/tiktok-profile-fetcher?keywords=${encodeURIComponent(keywords)}&count=${count}`,
        { 
          method: 'GET', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to search TikTok users: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.results) {
        return { success: false, error: 'No results found' };
      }

      const profiles: TikTokProfileData[] = data.results.map((result: any) => ({
        handle: result.handle || '',
        profile_image_url: result.profile_image_url || '',
        followers_count: result.followers_count ?? 0,
        hearts_count: 0,
        video_count: 0,
        user_id: result.user_id || '',
        name: result.name || '',
      }));

      return { success: true, profiles };
    } catch (error) {
      console.error('Error searching TikTok users:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  async enrichTikTokProfile(handle: string): Promise<PlatformServiceResponse<TikTokProfileData>> {
    try {
      const token = await userService.getAuthToken();
      
      const response = await fetch(
        `${PLATFORM_API_BASE_URL}/tiktok-profile-fetcher?unique_id=${encodeURIComponent(handle)}`,
        { 
          method: 'GET', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to enrich TikTok profile: ${response.status}`);
      }

      const data = await response.json();
      const profile: TikTokProfileData = {
        handle: data.handle || data.unique_id || handle,
        profile_image_url: data.profile_image_url || '',
        followers_count: data.followers_count ?? data.follower_count ?? 0,
        hearts_count: data.hearts_count ?? data.likes_count ?? 0,
        video_count: data.video_count ?? 0,
        user_id: data.user_id || data.id || '',
        engagement_rate: data.engagement_rate || undefined,
      };

      return { success: true, profile };
    } catch (error) {
      console.error('Error enriching TikTok profile:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  async searchInstagramUsers(search_query: string): Promise<PlatformServiceResponse<InstagramProfileData>> {
    try {
      const token = await userService.getAuthToken();
      
      const response = await fetch(
        `${PLATFORM_API_BASE_URL}/instagram-profile-fetcher?search_query=${encodeURIComponent(search_query)}`,
        { 
          method: 'GET', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to search Instagram users: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.results) {
        return { success: false, error: 'No results found' };
      }

      const profiles: InstagramProfileData[] = data.results.map((result: any) => ({
        handle: result.handle || '',
        profile_image_url: result.profile_image_url || '',
        followers_count: result.followers_count ?? 0,
        user_id: result.user_id || '',
      }));

      return { success: true, profiles };
    } catch (error) {
      console.error('Error searching Instagram users:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  async enrichInstagramProfile(handle: string): Promise<PlatformServiceResponse<InstagramProfileData>> {
    try {
      const token = await userService.getAuthToken();
      
      const response = await fetch(
        `${PLATFORM_API_BASE_URL}/instagram-profile-fetcher?username=${encodeURIComponent(handle)}`,
        { 
          method: 'GET', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to enrich Instagram profile: ${response.status}`);
      }

      const data = await response.json();
      const profile: InstagramProfileData = {
        handle: data.handle || data.username || handle,
        profile_image_url: data.profile_image_url || data.profile_image_source_url || '',
        followers_count: data.followers_count ?? 0,
        user_id: String(data.user_id || data.id || ''),
        engagement_rate: data.engagement_rate || undefined,
      };
      return { success: true, profile };
    } catch (error) {
      console.error('Error enriching Instagram profile:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  async enrichTwitchProfile(handle: string): Promise<PlatformServiceResponse<TwitchProfileData>> {
    try {
      const token = await userService.getAuthToken();
      
      const response = await fetch(
        `${PLATFORM_API_BASE_URL}/twitch-profile-fetcher?username=${encodeURIComponent(handle)}`,
        { 
          method: 'GET', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to enrich Twitch profile: ${response.status}`);
      }

      const data = await response.json();
      const profile: TwitchProfileData = {
        handle: data.login || data.handle || handle,
        profile_image_url: data.profile_image_url || '',
        followers_count: data.followers_count ?? 0,
        acv: data.acv ?? 0,
        user_id: data.id || '',
        broadcaster_type: data.broadcaster_type || '',
      };
      return { success: true, profile };
    } catch (error) {
      console.error('Error enriching Twitch profile:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
};

// Project relationship functions
export const addCreatorToProject = async (creatorId: string, projectUuid: string): Promise<void> => {
  try {
    const token = await userService.getAuthToken();
    
    const response = await fetch(`${PLATFORM_API_BASE_URL}/creator-project-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ creator_id: creatorId, project_uuid: projectUuid }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to add creator to project: ${response.status}`);
    }
  } catch (error) {
    console.error('Error adding creator to project:', error);
    throw error;
  }
};

export const removeCreatorFromProject = async (creatorId: string, projectUuid: string): Promise<void> => {
  try {
    const token = await userService.getAuthToken();
    
    // Send as query parameters since API Gateway may not handle DELETE body properly
    const queryParams = new URLSearchParams({
      creator_id: creatorId,
      project_uuid: projectUuid
    });
    
    const response = await fetch(`${PLATFORM_API_BASE_URL}/creator-project-service?${queryParams}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to remove creator from project: ${response.status}`);
    }
  } catch (error) {
    console.error('Error removing creator from project:', error);
    throw error;
  }
};

export const getCreatorsByProject = async (projectUuid: string): Promise<CreatorsResponse> => {
  try {
    const token = await userService.getAuthToken();
    const queryParams = new URLSearchParams();
    queryParams.set('project_uuid', projectUuid);

    const response = await fetch(`${BASE_URL}?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch creators for project: ${response.statusText}`);
    }

    const result = await response.json();
    
    return {
      data: result.creators || [],
      total: result.count || 0,
      filters: { project_uuid: projectUuid },
    };
  } catch (error) {
    console.error('Error fetching creators for project:', error);
    throw error;
  }
};

export default {
  getCreators,
  getCreator,
  createCreator,
  updateCreator,
  deleteCreator,
  platformService,
  addCreatorToProject,
  removeCreatorFromProject,
  getCreatorsByProject,
}; 